import { CssBaseline, Box, Typography, Container, Button, Paper } from '@mui/material';
import { RouterProvider } from 'react-router-dom';
import { useState, useEffect } from 'react';
import router from './router';
import { AuthProvider } from './hooks/useAuth';
import { ErrorBoundary } from './components/ErrorBoundary';
import { SettingsProvider } from './contexts/SettingsContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { VoiceModeProvider } from './contexts/VoiceModeContext';
import { GlobalFormProtection } from './contexts/GlobalFormProtection';
import { ChunkErrorHandler } from './utils/chunkErrorHandler';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import GoogleMapsProvider from './components/common/GoogleMapsProvider';
import './styles/google-maps-override.css';

// Debug function to safely stringify objects
const safeStringify = (obj: unknown, indent = 2) => {
  try {
    // Handle circular references
    const seen = new WeakSet();
    return JSON.stringify(obj, (key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return '[Circular Reference]';
        }
        seen.add(value);
      }
      return value;
    }, indent);
  } catch (err) {
    return `[Error stringifying object: ${err}]`;
  }
};

function App() {
  const [error, setError] = useState<Error | null>(null);
  const [errorDetails, setErrorDetails] = useState<string>('');

  // Initialize chunk error handler
  useEffect(() => {
    ChunkErrorHandler.setupGlobalErrorHandler();
  }, []);

  // Disable browser scroll restoration to prevent conflicts with our ScrollToTop component
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Add global error handler
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      console.error('Global error caught:', event.error);
      
      // Extract more details about the error
      const errorObj = event.error;
      setError(errorObj);
      
      // Try to get more context about the error
      try {
        // Check if it's a URL construction error
        if (errorObj.message.includes('URL')) {
          console.error('URL construction error detected');
          
          // Try to find the problematic URL
          const stack = errorObj.stack || '';
          const frames = stack.split('\n');
          
          // Log detailed information
          const details = {
            message: errorObj.message,
            stack: frames,
            name: errorObj.name,
            location: window.location.href,
            timestamp: new Date().toISOString()
          };
          
          console.error('Error details:', details);
          setErrorDetails(safeStringify(details));
        } else {
          setErrorDetails(errorObj.stack || errorObj.message);
        }
      } catch (detailError) {
        console.error('Error while extracting error details:', detailError);
        setErrorDetails('Failed to extract error details');
      }
      
      event.preventDefault();
    };

    // Add unhandled promise rejection handler
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled Promise Rejection:', event.reason);

      // Ignore MetaMask errors - they're from browser extensions
      const errorMessage = String(event.reason?.message || event.reason || '');
      if (errorMessage.includes('MetaMask') || errorMessage.includes('chrome-extension://')) {
        console.warn('Ignoring MetaMask/Extension error:', errorMessage);
        event.preventDefault();
        return;
      }

      setError(event.reason instanceof Error ? event.reason : new Error(String(event.reason)));
      setErrorDetails(event.reason instanceof Error ? event.reason.stack || '' : String(event.reason));
      event.preventDefault();
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);


  // If there's an error, show error screen
  if (error) {
    return (
      <ThemeProvider>
        <CssBaseline />
        <Container maxWidth="md" sx={{ mt: 8 }}>
          <Typography variant="h4" color="error" gutterBottom>
            Something went wrong
          </Typography>
          <Paper sx={{ bgcolor: 'background.paper', p: 3, borderRadius: 1, mb: 3 }}>
            <Typography variant="h6">Error details:</Typography>
            <Typography variant="body1" component="pre" sx={{ whiteSpace: 'pre-wrap', mt: 2 }}>
              {error.message}
            </Typography>
            
            <Typography variant="body2" sx={{ mt: 2 }}>
              Additional information:
            </Typography>
            <Box 
              component="pre" 
              sx={{ 
                whiteSpace: 'pre-wrap', 
                mt: 1, 
                p: 2, 
                bgcolor: 'rgba(0,0,0,0.1)', 
                borderRadius: 1,
                fontSize: '0.8rem',
                maxHeight: '300px',
                overflow: 'auto'
              }}
            >
              {errorDetails}
            </Box>
          </Paper>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => window.location.reload()}
            >
              Reload Application
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                // Navigate to home page
                window.location.href = window.location.origin + '/';
              }}
            >
              Go Home
            </Button>
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  // Normal application rendering
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <CssBaseline />
        <GoogleMapsProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <AuthProvider>
              <SettingsProvider>
                <NotificationProvider>
                  <VoiceModeProvider>
                    <GlobalFormProtection enabled={false} debugMode={false}>
                      <RouterProvider router={router} />
                    </GlobalFormProtection>
                  </VoiceModeProvider>
                </NotificationProvider>
              </SettingsProvider>
            </AuthProvider>
          </LocalizationProvider>
        </GoogleMapsProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
